import DigiobcanApi from '../api/DigiobcanApi';
import { useM } from '../lib/query';
import { shortId } from '../lib/ids';
import { useLogger } from '../providers/LoggerProvider';
import { __, toastError, toastSuccess } from '../utils';
import ConfirmationDialog from './ConfirmationDialog';
import { Stack, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

/** Podnet vybratý v zozname. Na archiváciu stačí identifikátor a názov. */
export type ArchiveReport = {
  reportId: string;
  title: string;
};

type Props = {
  /** Prázdne pole = dialóg je zavretý. Rovnaká dohoda ako v merge modále. */
  reports: ArchiveReport[];
  /** `archive` archivuje vybraté, `restore` ich vracia z archívu. */
  mode: 'archive' | 'restore';
  onClose: () => void;
  onSuccess: () => void;
};

/** „1 podnet" / „3 podnety" / „7 podnetov" - slovenské skloňovanie. */
const reportsLabel = (count: number) => {
  if (count === 1) return __('1 podnet');
  if (count >= 2 && count <= 4) return `${count} ${__('podnety')}`;
  return `${count} ${__('podnetov')}`;
};

/**
 * Hromadná archivácia a obnovenie podnetov z výberu v zozname.
 *
 * **Prečo hromadne.** Archivuje sa spam a omylom podané podnety, a tie
 * neprichádzajú po jednom - vlna sa v zozname označí zaškrtávacími políčkami
 * naraz. Prechádzať kvôli tomu detail za detailom je práca, ktorú si stroj
 * môže odpracovať sám.
 *
 * **`allSettled`, nie `all`.** Tu je rozdiel proti
 * [[AdminReportsMergeModal]], ktorý ide cez `Promise.all`: ten skladá jeden
 * vzťah a keď časť zlyhá, je to jedna pokazená operácia. Tu je každý podnet
 * samostatné rozhodnutie a `all` by pri prvej chybe zahodil výsledok
 * ostatných - úradník by nevedel, čo sa archivovalo a čo nie, a druhý pokus
 * nad už archivovaným podnetom skončí na `409 REPORT_ALREADY_ARCHIVED`.
 * Preto sa počty hlásia oddelene.
 *
 * Dialóg **nevypisuje podnety, ktoré sa preskočia**, lebo sa preskočiť nemá
 * žiadny: zoznam je vždy len archivované, alebo len nearchivované (filter
 * `archived` prepína, nezužuje), takže výber je z jednej strany a operácia
 * platí pre celý.
 */
const AdminReportsArchiveModal: React.FC<Props> = ({
  reports,
  mode,
  onClose,
  onSuccess,
}) => {
  const { logger } = useLogger();
  const archiving = mode === 'archive';

  const mutation = useM(
    async () => {
      const results = await Promise.allSettled(
        reports.map(({ reportId }) =>
          archiving
            ? DigiobcanApi.adminArchiveReport(reportId)
            : DigiobcanApi.adminRestoreReport(reportId),
        ),
      );

      const failed = results
        .map((result, index) => ({ result, report: reports[index] }))
        .filter(({ result }) => result.status === 'rejected');

      return { done: results.length - failed.length, failed };
    },
    {
      onSuccess: ({ done, failed }) => {
        if (done) {
          toastSuccess(
            archiving
              ? `${__('Archivované:')} ${reportsLabel(done)}`
              : `${__('Obnovené:')} ${reportsLabel(done)}`,
          );
        }

        if (failed.length) {
          // Menovite, nie len počet: úradník musí vedieť, ktorý podnet zostal
          // nedotknutý, inak by musel prejsť celý výber znova.
          const names = failed
            .map(({ report }) => shortId(report.reportId))
            .join(', ');

          logger.error('Bulk report archive partially failed', {
            action: archiving ? 'bulkArchiveReports' : 'bulkRestoreReports',
            data: { failed: names, total: reports.length },
          });

          toastError(
            archiving
              ? `${__('Nepodarilo sa archivovať:')} ${names}`
              : `${__('Nepodarilo sa obnoviť:')} ${names}`,
          );
        }

        onSuccess();
      },
      onError: (error) => {
        logger.error('Bulk report archive failed', {
          action: archiving ? 'bulkArchiveReports' : 'bulkRestoreReports',
          error,
          data: { total: reports.length },
        });
        toastError(__('Niečo sa pokazilo.'));
      },
    },
  );

  const run = () => {
    if (!navigator.onLine) {
      toastError(__('Nie ste pripojený k internetu.'));
      return;
    }
    mutation.mutate(undefined);
  };

  return (
    <ConfirmationDialog
      open={reports.length > 0}
      title={
        archiving
          ? `${__('Archivovať')} ${reportsLabel(reports.length)}?`
          : `${__('Obnoviť')} ${reportsLabel(reports.length)}?`
      }
      description={
        archiving
          ? __(
              'Podnety zmiznú z aplikácie - z mapy, z verejného zoznamu aj z „mojich podnetov" ich autorov. V databáze zostanú a dajú sa obnoviť.',
            )
          : __(
              'Podnety sa vrátia do aplikácie tak, ako boli pred archiváciou. Označenie „nevhodné" sa tým nemení.',
            )
      }
      confirmLabel={archiving ? __('Archivovať') : __('Obnoviť')}
      isLoading={mutation.isPending}
      onConfirm={run}
      onClose={onClose}
    >
      {/* Zoznam názvov, nie len počet: pri desiatich zaškrtnutých riadkoch je
          „Archivovať 10 podnetov?" tvrdenie, ktoré sa nedá overiť očami. */}
      <Stack gap={0.25} sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {reports.map((report) => (
          <Stack
            key={report.reportId}
            direction='row'
            gap={1}
            alignItems='baseline'
          >
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
            >
              {shortId(report.reportId)}
            </Typography>
            <Typography variant='body2' sx={{ minWidth: 0 }} noWrap>
              {report.title}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </ConfirmationDialog>
  );
};

export default AdminReportsArchiveModal;
